import type { ReactNode } from 'react'
import Image from 'next/image'
import { cn } from './cn'

/**
 * État vide.
 *
 * Le parti pris du handoff : un écran vide est un moment de marque, pas une
 * impasse. D'où l'illustration trait noir posée sur une forme lavande, et un
 * libellé qui dit ce qui va s'y trouver plutôt que ce qui manque.
 *
 * Règles d'emploi des illustrations, non négociables : trait noir sur forme
 * lavande — jamais teintée en lavande, jamais inversée en blanc, jamais sur
 * fond encre.
 */

/**
 * Les 40 illustrations du pack Umiros — trait noir et blanc, PNG transparent
 * 2000×2000. Le handoff n'en embarquait que 8 ; le design system complet les
 * fournit toutes, ce qui permet de ne pas répéter le même dessin d'un écran vide
 * à l'autre. Une illustration par bloc, jamais deux.
 */
export type Illustration =
  | 'ai-drawing'
  | 'ai-human-touch'
  | 'ai-thinking'
  | 'ai-tools'
  | 'ai-video-editor'
  | 'biz-collaboration'
  | 'biz-conference'
  | 'biz-networking'
  | 'biz-presentation'
  | 'biz-training-session'
  | 'biz-whiteboard-team'
  | 'creative-craft'
  | 'creative-gallery'
  | 'formation-ai-learning'
  | 'formation-art-creative'
  | 'formation-computer-skills'
  | 'formation-evening-classes'
  | 'formation-graduation'
  | 'formation-lecture-class'
  | 'formation-library'
  | 'formation-online-lecture'
  | 'formation-remote-student'
  | 'formation-study-desk'
  | 'formation-tutoring'
  | 'gesture-handshake'
  | 'gesture-high-five'
  | 'media-photography'
  | 'media-podcast'
  | 'success-climb-team'
  | 'success-passion'
  | 'success-star-rating'
  | 'success-team-trophy'
  | 'success-trophy'
  | 'tech-3d-printing'
  | 'tech-coding'
  | 'web-collab'
  | 'web-layout'
  | 'workspace-home-office'
  | 'workspace-remote-cat'
  | 'workspace-team'

export function EmptyState({
  illustration,
  shape = 'Umiros_Form_01',
  title,
  children,
  action,
  className,
}: {
  illustration: Illustration
  /** Alterner d'un écran à l'autre évite que tous les états vides se ressemblent. */
  shape?: 'Umiros_Form_01' | 'Umiros_Form_04'
  title: string
  /** Une phrase : ce que l'écran contiendra, ou ce qui déclenche son remplissage. */
  children?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-card px-6 py-12 text-center',
        className,
      )}
    >
      {/* La forme de marque sert de fond à l'illustration. On emploie l'asset
          livré plutôt qu'un rond dessiné en CSS, et on le pivote dans la plage
          de 5 à 15° prescrite par le handoff. `Form_01` et `Form_04` sont les
          deux blobs ; `Form_02` est l'étoile, trop découpée pour servir de fond
          à une illustration. */}
      <div className="relative flex size-40 items-center justify-center">
        <Image
          src={`/brand/shapes/${shape}.svg`}
          alt=""
          width={160}
          height={160}
          aria-hidden="true"
          className="absolute inset-0 size-40 -rotate-[9deg]"
        />
        <Image
          src={`/brand/illustrations/${illustration}.png`}
          alt=""
          width={112}
          height={112}
          className="relative size-28 object-contain"
        />
      </div>

      <div className="flex max-w-sm flex-col gap-1.5">
        <h3>{title}</h3>
        {children && <p className="text-[14px] text-ink-soft">{children}</p>}
      </div>

      {action}
    </div>
  )
}
